import { useState } from 'react'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { useTaxonomy } from '../../contexts/TaxonomyContext'
import type { ResourceType } from '../../types'

export interface FilterState {
  taxonomyFilter?: string
  resourceTypes: ResourceType[]
  dateFrom?: string
  dateTo?: string
  peerReviewed: boolean
  audience: string[]
}

interface FacetedFiltersProps {
  filters: FilterState
  onFilterChange: (filters: FilterState) => void
}

const RESOURCE_TYPE_OPTIONS: { id: ResourceType; label: string }[] = [
  { id: 'journal', label: 'Journal Articles' },
  { id: 'video', label: 'Videos' },
  { id: 'audio', label: 'Audio' },
  { id: 'image', label: 'Images' },
  { id: 'document', label: 'Documents' },
  { id: 'website', label: 'Websites' },
  { id: 'course', label: 'Courses' },
]

const AUDIENCE_OPTIONS = [
  { id: 'undergraduate', label: 'Undergraduate' },
  { id: 'postgraduate', label: 'Postgraduate' },
  { id: 'researcher', label: 'Researcher' },
  { id: 'professional', label: 'Professional' },
]

function FilterSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-gray-100 pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 hover:text-gray-900 transition"
      >
        {title}
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="mt-3 space-y-2">{children}</div>}
    </div>
  )
}

export default function FacetedFilters({ filters, onFilterChange }: FacetedFiltersProps) {
  const { taxonomies, activeTaxonomy } = useTaxonomy()

  const hasActiveFilters =
    filters.resourceTypes.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.peerReviewed ||
    filters.audience.length > 0 ||
    (filters.taxonomyFilter && filters.taxonomyFilter !== activeTaxonomy)

  function updateFilter(partial: Partial<FilterState>) {
    onFilterChange({ ...filters, ...partial })
  }

  function toggleResourceType(type: ResourceType) {
    const current = filters.resourceTypes
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type]
    updateFilter({ resourceTypes: updated })
  }

  function toggleAudience(audience: string) {
    const current = filters.audience
    const updated = current.includes(audience)
      ? current.filter((a) => a !== audience)
      : [...current, audience]
    updateFilter({ audience: updated })
  }

  function clearAll() {
    onFilterChange({
      taxonomyFilter: undefined,
      resourceTypes: [],
      dateFrom: undefined,
      dateTo: undefined,
      peerReviewed: false,
      audience: [],
    })
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-xs text-acad-600 hover:text-acad-700 transition font-medium"
          >
            <X className="w-3 h-3" />
            Clear all
          </button>
        )}
      </div>

      {/* Taxonomy filter */}
      <FilterSection title="Taxonomy">
        <select
          value={filters.taxonomyFilter || activeTaxonomy}
          onChange={(e) => updateFilter({ taxonomyFilter: e.target.value })}
          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500 bg-white"
        >
          {taxonomies.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </FilterSection>

      {/* Resource type checkboxes */}
      <FilterSection title="Resource Type">
        {RESOURCE_TYPE_OPTIONS.map((option) => (
          <label key={option.id} className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.resourceTypes.includes(option.id)}
              onChange={() => toggleResourceType(option.id)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-acad-500 focus:ring-acad-500"
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition">
              {option.label}
            </span>
          </label>
        ))}
      </FilterSection>

      {/* Date range */}
      <FilterSection title="Date Range" defaultOpen={false}>
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => updateFilter({ dateFrom: e.target.value || undefined })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => updateFilter({ dateTo: e.target.value || undefined })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500"
            />
          </div>
        </div>
      </FilterSection>

      {/* Peer reviewed toggle */}
      <FilterSection title="Quality">
        <label className="flex items-center gap-2 cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.peerReviewed}
            onChange={(e) => updateFilter({ peerReviewed: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-gray-300 text-acad-500 focus:ring-acad-500"
          />
          <span className="text-sm text-gray-600 group-hover:text-gray-900 transition">
            Peer reviewed only
          </span>
        </label>
      </FilterSection>

      {/* Audience filter */}
      <FilterSection title="Audience" defaultOpen={false}>
        {AUDIENCE_OPTIONS.map((option) => (
          <label key={option.id} className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.audience.includes(option.id)}
              onChange={() => toggleAudience(option.id)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-acad-500 focus:ring-acad-500"
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition">
              {option.label}
            </span>
          </label>
        ))}
      </FilterSection>
    </div>
  )
}
